"use client";
import { useState } from "react";
import { motion } from "framer-motion";
import { Users, Globe, Star, Map, Facebook, Instagram, X } from "lucide-react";
import Ceo from "./Ceo";
import home from "../accests/common/home.png";

// Sample memory images (replace with your images)
import mem1 from "../accests/g/g1 (12).jpeg";
import mem2 from "../accests/g/g1 (10).jpeg";
import mem3 from "../accests/g/g1 (16).jpeg";
import mem8 from "../accests/g/rw.jpeg";
import mem5 from "../accests/g/g1 (23).jpeg";
import Gallery from "./Gallery";
import {Link} from "react-router-dom";

export default function Home() {
    const features = [
        {
            icon: <Users size={28} className="text-yellow-400" />,
            title: "Customized Tours",
            desc: "Private & group tours tailored just for you.",
        },
        {
            icon: <Globe size={28} className="text-yellow-400" />,
            title: "Experienced Guides",
            desc: "Multilingual tour guides for smooth journeys.",
        },
        {
            icon: <Map size={28} className="text-yellow-400" />,
            title: "Authentic Experiences",
            desc: "Cultural and nature experiences you’ll never forget.",
        },
        {
            icon: <Star size={28} className="text-yellow-400" />,
            title: "Trusted Globally",
            desc: "Loved and trusted by travelers worldwide for over a decade.",
        },
    ];

    const images = [mem1, mem2, mem3, ,mem5,mem8];

    const [isOpen, setIsOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);

    const openModal = (img) => {
        setSelectedImage(img);
        setIsOpen(true);
    };

    const closeModal = () => {
        setIsOpen(false);
        setSelectedImage(null);
    };


    return (
        <div className="overflow-hidden">
            {/* HERO SECTION */}
            <section className="relative h-screen flex items-center justify-center">
                <div className="w-full h-screen relative">
                    <img
                        src={home}
                        alt="Sri Lanka nature"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50"></div>

                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1 }}
                        className="absolute inset-0 flex flex-col items-center justify-center text-center px-6"
                    >
                        <h1 className="text-5xl md:text-6xl text-white font-bold mb-4">
                            Explore Sri Lanka with Ranmali Tours
                        </h1>
                        <p className="text-white text-lg md:text-xl mb-6">
                            Luxury, adventure, and cultural experiences tailored just for you.
                        </p>
                        <a
                            href="/tours"
                            className="bg-yellow-400 px-8 py-3 rounded-full font-semibold shadow-lg hover:scale-105 transition"
                        >
                            View Tours
                        </a>
                    </motion.div>
                </div>
            </section>

            {/* WHY CHOOSE US SECTION */}
            <motion.section
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                className="py-20 px-6 bg-gray-50 text-gray-800"
            >
                <div data-aos="zoom-in-up" className="max-w-6xl mx-auto text-center mb-12">
                    <h2 className="text-4xl font-bold mb-4">Why Choose Us?</h2>
                    <p className="text-lg text-gray-700 leading-relaxed">
                        Discover the perfect blend of personalized service, local expertise, and unforgettable
                        experiences.
                    </p>
                </div>

                {/* FEATURES */}
                <div  data-aos="fade-up" className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
                    {features.map((feature, index) => (
                        <motion.div
                            key={index}
                            whileHover={{ scale: 1.05 }}
                            className="bg-white rounded-xl shadow-lg p-6 flex flex-col items-center text-center transition"
                        >
                            <div className="mb-4">{feature.icon}</div>
                            <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                            <p className="text-gray-600 text-sm">{feature.desc}</p>
                        </motion.div>
                    ))}
                </div>

                {/* SINGLE READ MORE BUTTON */}
                <div data-aos="fade-up" className="text-center mt-12">
                    <a
                        href="/about"
                        className="inline-block bg-yellow-400 px-8 py-3 rounded-full text-base font-semibold shadow hover:scale-105 transition"
                    >
                        Read More
                    </a>
                </div>
            </motion.section>


                {/* OUR MEMORIES SECTION - Bento Grid Layout */}
                <section data-aos="zoom-in-up" className="py-20 px-6 bg-white text-gray-800">
                    <div className="max-w-6xl mx-auto text-center mb-12">
                        <h2 className="text-4xl font-bold mb-4">Our Memories</h2>
                        <p className="text-lg text-gray-700 leading-relaxed">
                            Cherish some moments from our tours and adventures.!
                        </p>
                    </div>

                    <div data-aos="fade-up"
                          className="grid grid-cols-1 md:grid-cols-4 gap-4 max-w-6xl mx-auto">
                        {/* Large item */}
                        <motion.div
                            className="md:col-span-2 md:row-span-2 relative overflow-hidden rounded-2xl shadow-lg group cursor-pointer"
                            onClick={() => openModal(images[0])}
                            whileHover={{ scale: 1.05 }}
                        >
                            <img
                                src={images[0]}
                                alt="Memory 1"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-60 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                <div className="absolute bottom-0 left-0 right-0 p-4">
                                    <h3 className="text-2xl font-bold text-white">Happy Moment</h3>
                                    <p className="text-white"></p>
                                </div>
                            </div>

                        </motion.div>

                        {/* Two small items */}
                        {images.slice(1, 3).map((img, idx) => (
                            <motion.div
                                key={idx}
                                className="relative overflow-hidden rounded-2xl shadow-lg group cursor-pointer"
                                onClick={() => openModal(img)}
                                whileHover={{ scale: 1.05 }}
                            >
                                <img src={img} alt={`Memory ${idx + 2}`} className="w-full h-48 object-cover" />
                                <div className="absolute inset-0 bg-black bg-opacity-60 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <div className="absolute bottom-0 left-0 right-0 p-4">
                                        <h4 className="text-xl font-bold text-white">
                                            {idx === 0 ? "Sigiriya Village Tour" : "Ella"}
                                        </h4>
                                    </div>
                                </div>
                            </motion.div>
                        ))}

                        {/* Three medium items */}
                        {images.slice(3, 6).map((img, idx) => (
                            <motion.div
                                key={idx}
                                className="relative overflow-hidden rounded-2xl shadow-lg group cursor-pointer"
                                onClick={() => openModal(img)}
                                whileHover={{ scale: 1.05 }}
                            >
                                <img src={img} alt={`Memory ${idx + 4}`} className="w-full h-48 object-cover" />
                                <div className="absolute inset-0 bg-black bg-opacity-60 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <div className="absolute bottom-0 left-0 right-0 p-4">
                                        <h4 className="text-xl font-bold text-white">
                                            {idx === 0 ? "Travel Adventures" : idx === 1 ? "Bentota" : "Rawana waterfall"}
                                        </h4>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* See More Button */}
                    <div className="text-center mt-14">
                        <Link
                            to="/gallery"
                            className="inline-block bg-black text-white px-10 py-3 rounded-xl font-semibold hover:bg-gray-800 transition"
                        >
                            More
                        </Link>
                    </div>

                    {/* Modal */}
                    {isOpen && selectedImage && (
                        <div
                            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-auto"
                            onClick={closeModal}
                        >
                            <motion.div
                                className="relative bg-white rounded-xl max-w-3xl w-full overflow-hidden"
                                initial={{ opacity: 0, y: 50 }}
                                animate={{ opacity: 1, y: 0 }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* Close Button */}
                                <button
                                    className="absolute top-4 right-4 text-gray-700 hover:text-gray-900 z-50"
                                    onClick={closeModal}
                                >
                                    <X size={24} />
                                </button>

                                {/* Image */}
                                <img
                                    src={selectedImage}
                                    alt="Memory"
                                    className="w-full object-cover max-h-[80vh]" // limit height
                                />

                                {/* Social Media Icons */}
                                <div className="flex justify-center gap-6 py-4 bg-gray-100">
                                    <a
                                        href="https://www.facebook.com/share/18DNkUtQAg/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800"
                                    >
                                        <Facebook size={28} />
                                    </a>
                                    <a
                                        href="https://www.instagram.com/ranmali_tours/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-pink-500 hover:text-pink-700"
                                    >
                                        <Instagram size={28} />
                                    </a>
                                </div>
                            </motion.div>
                        </div>
                    )}

                </section>




            {/* CEO / Booking Section */}
            <Ceo />
        </div>
    );
}
